"use client";

import Image from "next/image";
import { contacts, footer, Section, socials } from "@/lib/data";
import Input from "./ui/input";
import InlineButton from "./ui/inline-button";
import Link from "next/link";
import { cn, withUrl } from "@/lib/utils";
import { useActionState } from "react";
import {
  create,
  NewsletterEmailOnlyErrors,
} from "./newsletter-email-only-actions";
import { useEffect } from "react";
import { toast } from "sonner";
import LINKS from "@/lib/links";
import { useWindowSize } from "@/hooks/use-window-size";

const initialState: {
  status: string;
  errors?: NewsletterEmailOnlyErrors;
  message?: string;
} = { status: "" };

export type FooterSection = {
  rows: {
    heading: string;
    links: {
      label: string;
      href: string;
    }[];
  }[];
};

export interface FooterProps {
  sections?: FooterSection[];
}

export default function Footer({ sections }: FooterProps) {
  const size = useWindowSize();

  return (
    <footer className="flex flex-col gap-4 border-t-4 border-primary-500 bg-white md:gap-2">
      <div className="flex flex-col gap-8 md:gap-10">
        <div className="mx-auto flex w-full max-w-lg flex-col flex-wrap justify-between gap-6 px-2 pt-6 md:flex-row md:gap-y-10 md:px-6">
          <LogoAndSubscribe />
          <ContactUs />
          {sections && sections?.length > 0 ? (
            <>
              {size === "lg" ? (
                <SectionsLg sections={sections} />
              ) : size === "md" ? (
                <SectionsMd sections={sections} />
              ) : (
                <SectionsSm sections={sections} />
              )}
            </>
          ) : (
            <Sections sections={footer.sections} />
          )}
        </div>
      </div>
      <BottomFooter />
    </footer>
  );
}

function LogoAndSubscribe() {
  return (
    <div className="flex h-fit flex-1 flex-col px-2 md:px-0 lg:w-[268px]">
      <Image
        src={footer.logo}
        alt="Care Indeed Logo"
        width={220}
        height={88}
      />
      <SubscribeForm />
    </div>
  );
}

function ContactUs() {
  return (
    <div className="flex flex-1 flex-col justify-start px-2 md:px-0">
      <p className="mb-2 font-montserrat text-xl font-semibold text-neutral-500 lg:mb-4 lg:text-subheading-2">
        Contact Us
      </p>
      <InlineButton
        className="mr-auto px-0"
        color="orange"
        size="md"
        asChild
      >
        <Link
          href={withUrl(LINKS.inquiryForm.href)}
          prefetch
        >
          Inquiry Form
        </Link>
      </InlineButton>
      <ul className="mt-2 flex flex-col gap-y-2 text-neutral-500 lg:mb-2 lg:mt-4 lg:gap-y-4">
        {contacts.map((contact, index) => (
          <li
            key={`contact-${contact.title}-${index}`}
            className="flex flex-row items-center gap-2 font-roboto text-body-1-300"
          >
            {contact.icon}
            <a
              href={contact.url}
              id={contact.id}
            >
              {contact.title}
            </a>
          </li>
        ))}
        <li className="mt-2 flex flex-row items-center gap-4 lg:mt-0">
          {socials.map((social, index) => (
            <Link
              key={`social-${social.url}-${index}`}
              href={social.url}
              prefetch
              className="flex size-8 items-center justify-center rounded-full border border-secondary-500 text-secondary-500"
            >
              <span className="sr-only">{social.name}</span>
              {social.icon}
            </Link>
          ))}
        </li>
      </ul>
    </div>
  );
}

function SectionsSm({ sections }: { sections: FooterSection[] }) {
  return (
    <div className="flex w-full flex-col gap-y-4">
      {sections.flatMap((section, sectionIndex) =>
        section.rows.map((row, rowIndex) => (
          <div key={`section-${sectionIndex}-row-${rowIndex}`}>
            <p className="mb-2 px-2 font-montserrat text-xl font-semibold text-neutral-500 lg:mb-4 lg:text-subheading-2">
              {row.heading}
            </p>
            <ul className="chromatic-ignore flex flex-col">
              {row.links.map((link, index) => (
                <li
                  className="flex w-full"
                  key={`${link.label}-${index}`}
                >
                  <InlineButton
                    color="teal"
                    href={withUrl(link.href)}
                  >
                    {link.label}
                  </InlineButton>
                </li>
              ))}
            </ul>
          </div>
        ))
      )}
    </div>
  );
}

function SectionsMd({ sections }: { sections: FooterSection[] }) {
  return (
    <div className="grid w-full auto-cols-fr grid-flow-col gap-x-10">
      {sections.flatMap((section, sectionIndex) =>
        section.rows.map((row, rowIndex) => (
          <div key={`section-${sectionIndex}-row-${rowIndex}`}>
            <p className="mb-2 px-2 font-montserrat text-xl font-semibold text-neutral-500 lg:mb-4 lg:text-subheading-2">
              {row.heading}
            </p>
            <ul className="chromatic-ignore flex flex-col">
              {row.links.map((link, index) => (
                <li
                  className="flex w-full"
                  key={`${link.label}-${index}`}
                >
                  <InlineButton
                    color="teal"
                    href={withUrl(link.href)}
                  >
                    {link.label}
                  </InlineButton>
                </li>
              ))}
            </ul>
          </div>
        ))
      )}
    </div>
  );
}

function SectionsLg({ sections }: { sections: FooterSection[] }) {
  return (
    <div className="grid w-full grid-cols-1 gap-x-10 gap-y-4 md:auto-cols-fr md:grid-flow-col md:gap-y-0 lg:w-auto lg:auto-cols-max lg:auto-rows-fr">
      {sections.map((section, index) => (
        <div
          key={`section-${index}`}
          className={cn("col-span-1 flex flex-col gap-8 md:row-span-5")}
        >
          {section.rows.map((row, index) => (
            <div key={`row-${index}`}>
              <p className="mb-2 px-2 font-montserrat text-subheading-3-500 text-neutral-500 lg:mb-4 lg:text-subheading-2">
                {row.heading}
              </p>
              <ul className="chromatic-ignore flex flex-col">
                {row.links.map((link, index) => (
                  <li
                    className="col-span-1 flex w-full"
                    key={`${link.label}-${index}`}
                  >
                    <InlineButton
                      color="teal"
                      href={withUrl(link.href)}
                    >
                      {link.label}
                    </InlineButton>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function Sections({ sections }: { sections: Section[] }) {
  return (
    <div className="grid w-full grid-cols-1 gap-x-10 gap-y-4 md:auto-cols-fr md:grid-flow-col md:gap-y-0 lg:w-auto lg:auto-cols-max lg:auto-rows-fr">
      {sections.map((section, index) => (
        <div
          key={`${section.header}-${index}`}
          className={cn(
            "col-span-1 md:row-span-5",
            section?.header === "Careers" && "md:row-span-5 lg:row-span-2",
            section?.header === "Legal" && "md:row-span-5 lg:row-span-3"
          )}
        >
          <p className="mb-2 px-2 font-montserrat text-subheading-3-500 text-neutral-500 lg:mb-4 lg:text-subheading-2">
            {section.header}
          </p>
          <ul className="chromatic-ignore flex flex-col">
            {section.menus.map((menu, index) => (
              <li
                className="col-span-1 flex w-full"
                key={`${menu.title}-${index}`}
              >
                <InlineButton
                  color="teal"
                  asChild
                >
                  <Link
                    href={withUrl(menu.url)}
                    prefetch
                  >
                    {menu.title}
                  </Link>
                </InlineButton>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

function SubscribeForm() {
  const [state, formAction, isPending] = useActionState(create, initialState);

  useEffect(() => {
    if (state?.message) {
      toast.error(state?.message);
    }
  }, [state]);

  return (
    <form
      id="newsletter-footer-form"
      aria-label="subscribe-form"
      className="mt-8 flex w-full flex-col md:w-[328px] lg:mt-6 lg:w-full"
      action={formAction}
    >
      <label
        htmlFor="newsletter-footer-email"
        className="mb-2 font-montserrat text-subheading-3-500 text-neutral-600 lg:mb-4"
      >
        JOIN 34,000+ Care Indeed Community
      </label>
      {state.status === "success" ? (
        <p className="font-montserrat text-lg font-normal text-secondary-600">
          Thank you for subscribing to our newsletter!
        </p>
      ) : (
        <>
          <Input
            baseInputClassName="rounded-2xl py-2 w-full"
            variant="border"
            type="email"
            id="newsletter-footer-email"
            name="email"
            aria-label="email"
            autoComplete="email"
            placeholder="Email address"
            required
            error={state.errors?.email && state.errors.email.length > 0}
            errorMessage={
              state.errors?.email && state.errors.email.length > 0
                ? state.errors.email.join(", ")
                : "Email is required"
            }
          />
          <InlineButton
            className="mt-2 w-fit px-0 text-start lg:mt-1"
            color="orange"
            size="md"
            disabled={isPending}
          >
            Subscribe to Our Newsletter
          </InlineButton>
        </>
      )}
    </form>
  );
}

function BottomFooter() {
  return (
    <>
      <div className="flex w-full border-t" />
      <div className="text-neutral-4 00 mx-auto flex w-full max-w-lg px-4 py-4 font-roboto text-body-2-300 md:px-6">
        {new Date().getFullYear()} Care Indeed, Inc. All Rights Reserved.
      </div>
    </>
  );
}
